import type { ResumoOrcamentosResponse } from '@triade/contracts';
import { formatarContagem, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface CartoesResumoProps {
  resumo: ResumoOrcamentosResponse | undefined;
  carregando: boolean;
  erro: string | null;
  onTentar: () => void;
  /** Filtro "abaixo da meta" ligado. */
  abaixoAtivo: boolean;
  onAbaixo: (ligado: boolean) => void;
}

const CARTAO = 'rounded-2xl border border-border bg-card px-[18px] py-4';
const VALOR = 'tabular mt-0.5 text-[22px] font-semibold whitespace-nowrap';

function orcamentos(n: number): string {
  return `${formatarContagem(n)} ${n === 1 ? 'orçamento' : 'orçamentos'}`;
}

/** Quatro cartoes do topo (docs/design/Orcamentos.dc.html). Todos os numeros sao agregados do servidor (GET /orcamentos/resumo). */
export function CartoesResumo({ resumo, carregando, erro, onTentar, abaixoAtivo, onAbaixo }: CartoesResumoProps) {
  if (erro) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-destructive/30 bg-danger-bg px-5 py-3 text-small text-danger-ink">
        <span>Não foi possível carregar o resumo: {erro}</span>
        <button type="button" onClick={onTentar} className="rounded-lg border border-border bg-card px-3 py-1 font-medium text-ink hover:bg-line-soft">
          Tentar de novo
        </button>
      </div>
    );
  }
  const pendente = !resumo;
  const nd = (v: React.ReactNode) => (pendente ? <span className="text-ink-disabled">{carregando ? '…' : '—'}</span> : v);
  const aberto = resumo?.emAberto;
  const aprovados = resumo?.porStatus.APROVADO;
  return (
    <section aria-label="Resumo dos orçamentos" aria-busy={carregando} className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
      <div className={CARTAO} data-testid="cartao-aberto">
        <div className="text-small text-ink-muted">Em aberto</div>
        <div className={VALOR}>{nd(formatarMoeda(aberto?.total))}</div>
        <div className="text-caption text-ink-secondary">{nd(aberto ? orcamentos(aberto.quantidade) : null)}</div>
      </div>
      <div className={CARTAO} data-testid="cartao-aprovados">
        <div className="text-small text-ink-muted">Aprovados</div>
        <div className={VALOR}>{nd(formatarMoeda(aprovados?.total))}</div>
        <div className="text-caption text-ink-secondary">{nd(aprovados ? orcamentos(aprovados.quantidade) : null)}</div>
      </div>
      <div className={CARTAO} data-testid="cartao-lucro-medio">
        <div className="text-small text-ink-muted">Lucro médio aprovado</div>
        <div className={VALOR}>{nd(resumo?.lucroMedioAprovado ? formatarFracaoPercentual(resumo.lucroMedioAprovado) : '—')}</div>
        <div className="text-caption text-ink-secondary">ponderado pela receita</div>
      </div>
      <div className={cn(CARTAO, 'border-orange-200 bg-warning-bg')} data-testid="cartao-abaixo">
        <div className="text-small text-warning-ink">Abaixo da meta</div>
        <div className={cn(VALOR, 'text-orange-900')}>
          {nd(resumo ? `${formatarContagem(resumo.abaixoDaMeta.quantidade)} de ${formatarContagem(resumo.abaixoDaMeta.base)}` : null)}
        </div>
        <button
          type="button"
          aria-pressed={abaixoAtivo}
          onClick={() => onAbaixo(!abaixoAtivo)}
          className="text-caption font-medium text-warning-ink underline-offset-2 hover:underline"
        >
          {abaixoAtivo ? 'Mostrar todos' : 'Ver só esses'}
        </button>
      </div>
    </section>
  );
}
